import {
  Box,
  Button,
  Flex,
  Grid,
  Image,
  SimpleGrid,
  Skeleton,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { SortDirection } from "@intentsify/types";
import { useQuery } from "@tanstack/react-query";
import { LogoBoxed, Modal, NoData } from "components";
import { accountLogoFallbackImage } from "const";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import {
  getQuantumDemandDomainContacts,
  getQuantumDemandDomainMeta,
  getQuantumDemandDomainPersonas,
  getQuantumDemandDomainTopics,
  listPaginatedQuantumDemand,
} from "../../QuantumDemand.requests";
import { AccountLevelActivation } from "../AccountLevelActivation";
import { ProfileSectionCard } from "../ProfileSectionCard";
import { QuantumDemandContactCard } from "../QuantumDemandContactCard";
import { QuantumIndexGauge } from "../QuantumIndexGauge";
import { RankedPersonaList } from "../RankedPersonaList";
import { RankedTopicList } from "../RankedTopicList";
import { useBuyingGroupProfileParam } from "./BuyingGroupProfile.hooks";

const INITIAL_VISIBLE_CONTACTS = 6;
const VISIBLE_CONTACTS_INCREMENT = 3;
const INITIAL_VISIBLE_PERSONAS = 5;
const INITIAL_VISIBLE_TOPICS = 10;
const PROFILE_CONTENT_MAX_WIDTH = "1240px";
const TWO_COLUMNS_ABOVE_900PX = {
  "@media (min-width: 900px)": {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
};

interface BuyingGroupProfileModalProps {
  campaignId: number;
  domain: string | null;
}

export const BuyingGroupProfileModal = ({
  campaignId,
  domain,
}: BuyingGroupProfileModalProps) => {
  const { closeBuyingGroupProfile } = useBuyingGroupProfileParam();
  const modalBackground = useColorModeValue("gray.50", "gray.800");

  const { data, isFetching: isFetchingDomainRow } = useQuery(
    ["quantumDemandDomainRow", campaignId, domain],
    async () => {
      const { results } = await listPaginatedQuantumDemand(campaignId, {
        page: 1,
        per_page: 1,
        order_by: "quantumIndex",
        order: SortDirection.DESC,
        domains: [String(domain)],
      });

      return results[0] ?? null;
    },
    { enabled: Boolean(domain) },
  );

  const { data: domainMeta, isFetching: isFetchingDomainMeta } = useQuery(
    ["quantumDemandDomainMeta", campaignId, domain],
    () => getQuantumDemandDomainMeta(campaignId, String(domain)),
    { enabled: Boolean(domain) },
  );

  const { data: contacts = [], isFetching: isFetchingContacts } = useQuery(
    ["quantumDemandDomainContacts", campaignId, domain],
    () => getQuantumDemandDomainContacts(campaignId, String(domain)),
    { enabled: Boolean(domain) },
  );

  const { data: domainPersonas, isLoading: isLoadingPersonas } = useQuery(
    ["quantumDemandDomainPersonas", campaignId, domain],
    () => getQuantumDemandDomainPersonas(campaignId, String(domain)),
    { enabled: Boolean(domain) },
  );

  const { data: domainTopics, isLoading: isLoadingTopics } = useQuery(
    ["quantumDemandDomainTopics", campaignId, domain],
    () => getQuantumDemandDomainTopics(campaignId, String(domain)),
    { enabled: Boolean(domain) },
  );

  const [visibleContactsCount, setVisibleContactsCount] = useState(
    INITIAL_VISIBLE_CONTACTS,
  );

  useEffect(() => {
    setVisibleContactsCount(INITIAL_VISIBLE_CONTACTS);
  }, [campaignId, domain]);

  const firmographics = [
    domainMeta?.industry,
    domainMeta?.employeeRange && `${domainMeta.employeeRange} employees`,
    domainMeta?.revenueRange && `${domainMeta.revenueRange} revenue`,
  ].filter(Boolean);

  const renderContacts = () => {
    if (isFetchingContacts) {
      return <Skeleton h="240px" borderRadius="lg" />;
    }

    if (contacts.length > 0) {
      return (
        <>
          <Grid
            templateColumns={{
              base: "minmax(0, 1fr)",
              xl: "repeat(2, minmax(0, 1fr))",
            }}
            gap={6}
          >
            {contacts.slice(0, visibleContactsCount).map((contact) => (
              <QuantumDemandContactCard key={contact.email} contact={contact} />
            ))}
          </Grid>
          {visibleContactsCount < contacts.length && (
            <Flex justifyContent="center" mt={4}>
              <Button
                size="sm"
                onClick={() =>
                  setVisibleContactsCount(
                    (count) => count + VISIBLE_CONTACTS_INCREMENT,
                  )
                }
              >
                Show more
              </Button>
            </Flex>
          )}
        </>
      );
    }

    return (
      <NoData
        height="240px"
        message="Buying group profile data is not available yet."
      />
    );
  };

  const renderBuyingGroupIntelligence = () => {
    if (isLoadingPersonas) {
      return (
        <ProfileSectionCard title="Persona-Level Intelligence" isCollapsible>
          <Skeleton h="200px" borderRadius="lg" />
        </ProfileSectionCard>
      );
    }

    if (
      !domainPersonas ||
      (domainPersonas.buyingGroupPersonas.length === 0 &&
        domainPersonas.otherPersonas.length === 0)
    ) {
      return null;
    }

    return (
      <ProfileSectionCard title="Persona-Level Intelligence" isCollapsible>
        <SimpleGrid columns={1} spacing={7} sx={TWO_COLUMNS_ABOVE_900PX}>
          <RankedPersonaList
            title="Active Personas within Buying Group"
            personas={domainPersonas.buyingGroupPersonas}
            initialCount={INITIAL_VISIBLE_PERSONAS}
            emptyMessage="No buying group personas researched last week."
          />
          <RankedPersonaList
            title="Other Active Personas"
            personas={domainPersonas.otherPersonas}
            initialCount={INITIAL_VISIBLE_PERSONAS}
            emptyMessage="No other personas researched last week."
          />
        </SimpleGrid>
      </ProfileSectionCard>
    );
  };

  const renderAccountIntelligence = () => {
    if (isLoadingTopics) {
      return (
        <ProfileSectionCard title="Account-Level Intelligence" isCollapsible>
          <Skeleton h="200px" borderRadius="lg" />
        </ProfileSectionCard>
      );
    }

    if (
      !domainTopics ||
      (domainTopics.topics.length === 0 && domainTopics.solutions.length === 0)
    ) {
      return null;
    }

    return (
      <ProfileSectionCard title="Account-Level Intelligence" isCollapsible>
        <SimpleGrid columns={1} spacing={7} sx={TWO_COLUMNS_ABOVE_900PX}>
          <RankedTopicList
            title="Topics Researched"
            columnLabel="Topic"
            topics={domainTopics.topics}
            initialCount={INITIAL_VISIBLE_TOPICS}
            emptyMessage="No topics researched last week."
          />
          <RankedTopicList
            title="Solutions Researched"
            columnLabel="Solution"
            topics={domainTopics.solutions}
            initialCount={INITIAL_VISIBLE_TOPICS}
            emptyMessage="No solutions researched last week."
          />
        </SimpleGrid>
      </ProfileSectionCard>
    );
  };

  return (
    <Modal
      size="viewContainer"
      isOpen={Boolean(domain)}
      onClose={closeBuyingGroupProfile}
      backgroundColor={modalBackground}
      body={
        <Box pr={4}>
          <Flex
            key={domain}
            direction="column"
            gap="14px"
            maxW={PROFILE_CONTENT_MAX_WIDTH}
            mx="auto"
            pl={4}
          >
            <ProfileSectionCard>
              <Grid
                templateAreas={{
                  base: `"company" "quantumIndex" "logo"`,
                  md: `"company company" "quantumIndex logo"`,
                  lg: `"company quantumIndex logo"`,
                }}
                templateColumns={{
                  base: "1fr",
                  md: "1fr 1fr",
                  lg: "auto",
                  xl: "minmax(0, 1fr) auto minmax(0, 1fr)",
                }}
                alignItems="center"
                gap={{ base: 6, md: 4, lg: 6 }}
              >
                <Flex gridArea="company" alignItems="center" gap={4} minW={0}>
                  <Box borderRadius={4} bg="white" p={1}>
                    <Image
                      minW="35px"
                      boxSize="35px"
                      objectFit="contain"
                      src={data?.logoUrl}
                      fallbackSrc={accountLogoFallbackImage}
                    />
                  </Box>
                  <Flex direction="column" gap={1} flex={1} minW={0}>
                    <Skeleton isLoaded={!isFetchingDomainMeta}>
                      <Text fontWeight="bold">
                        {domainMeta?.companyName ?? domain}
                      </Text>
                    </Skeleton>
                    <Skeleton isLoaded={!isFetchingDomainMeta}>
                      <Text fontWeight="bold" fontSize={12} color={"gray.400"}>
                        {firmographics.join(" • ")}
                      </Text>
                    </Skeleton>
                    <Skeleton isLoaded={!isFetchingDomainMeta}>
                      <Text fontWeight="bold" fontSize={12}>
                        Intent Model: {domainMeta?.campaignName}
                      </Text>
                    </Skeleton>
                    <Skeleton isLoaded={!isFetchingDomainMeta}>
                      <Text fontWeight="bold" fontSize={12} color={"teal.500"}>
                        {domainMeta?.buyerResearchStage
                          ? `Buyer Research Stage: ${domainMeta?.buyerResearchStage}`
                          : ""}
                      </Text>
                    </Skeleton>
                  </Flex>
                </Flex>
                <Flex
                  gridArea="quantumIndex"
                  alignItems="center"
                  gap={3}
                  justifySelf={{ base: "center", md: "start", xl: "center" }}
                >
                  {data && (
                    <>
                      <Box boxSize="64px">
                        <QuantumIndexGauge value={data.quantumIndex} />
                      </Box>
                      <Text
                        textTransform="uppercase"
                        fontWeight="bold"
                        fontSize={14}
                      >
                        quantum index
                      </Text>
                    </>
                  )}
                </Flex>
                <Flex
                  direction="column"
                  justifyContent="space-between"
                  gridArea="logo"
                  justifySelf={{ base: "center", md: "end" }}
                  w={180}
                  gap={2}
                >
                  <LogoBoxed adaptToDarkMode={true} />
                  <Box
                    border="1px"
                    borderColor="teal.500"
                    borderRadius="full"
                    opacity={0.7}
                    textAlign="center"
                    py={2}
                    px={4}
                  >
                    <Flex alignItems="center" gap={3}>
                      <Box
                        background="teal.500"
                        borderRadius="full"
                        w={1.5}
                        h={1.5}
                      ></Box>
                      <Skeleton isLoaded={!isFetchingDomainRow}>
                        <Text fontSize={12}>
                          Last updated:{" "}
                          {DateTime.now().startOf("week").toFormat("MMM dd")}
                        </Text>
                      </Skeleton>
                    </Flex>
                  </Box>
                </Flex>
              </Grid>
            </ProfileSectionCard>
            {domain && (
              <ProfileSectionCard
                title="Account-Level Activation"
                isCollapsible
                defaultIsOpen={false}
              >
                <AccountLevelActivation
                  campaignId={campaignId}
                  domain={domain}
                />
              </ProfileSectionCard>
            )}
            <ProfileSectionCard title="Buying Group Engagement" isCollapsible>
              {renderContacts()}
            </ProfileSectionCard>
            {renderBuyingGroupIntelligence()}
            {renderAccountIntelligence()}
          </Flex>
        </Box>
      }
    />
  );
};
